import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth/AuthContext';
import { supabase } from '../lib/supabaseClient';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);
  
  // Check if there's a hash in the URL which indicates we're completing a reset
  useEffect(() => {
    // The hash contains the access token when user clicks the reset link in their email
    const hash = window.location.hash;
    if (hash && hash.substring(1).split('&').some(param => param.startsWith('type=recovery'))) {
      setIsResetMode(true);
    }
  }, []);
  
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      console.log('Sending reset password request for:', email);
      
      const { error: resetError, success } = await resetPassword(email);
      
      console.log('Reset request result:', { resetError, success });
      
      if (resetError) {
        console.error('Reset error details:', resetError);
        setError(`Failed to send reset instructions: ${resetError.message || 'Unknown error'}`);
      } else if (success) {
        setMessage('Password reset instructions have been sent to your email. Please check your inbox and spam folder.');
        setEmail('');
      }
    } catch (err: any) {
      console.error('Unexpected error during reset:', err);
      setError(`An unexpected error occurred: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      setError('Please enter a new password');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });
      
      if (updateError) {
        setError(updateError.message || 'Failed to update password');
      } else {
        setMessage('Your password has been updated successfully');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#232347] font-serif">
            {isResetMode ? 'Set New Password' : 'Reset Password'}
          </h2>
          <p className="mt-2 text-sm text-[#232347]/70">
            {isResetMode 
              ? 'Enter your new password below'
              : 'Enter your email address and we\'ll send you instructions to reset your password'
            }
          </p>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}
        
        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
            {message}
          </div>
        )}
        
        {isResetMode ? (
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#232347]">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-[#B2D8C5] bg-[#F8F6F2] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#232347]">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-[#B2D8C5] bg-[#F8F6F2] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
              />
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#3A5E63] hover:bg-[#2A4E53] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B2D8C5] transition-colors disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRequestReset} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#232347]">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-[#B2D8C5] bg-[#F8F6F2] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
              />
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#3A5E63] hover:bg-[#2A4E53] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B2D8C5] transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Instructions'}
              </button>
            </div>
          </form>
        )}
        
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-[#3A5E63] hover:text-[#2A4E53] transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 